import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType, type AuthenticationResult } from '@azure/msal-browser';
import { useEffect, useState, type ReactNode } from 'react';
import { msalConfig } from './config';

/** Where the user was headed before being bounced to sign-in. */
const RETURN_TO_KEY = 'mlp.staff.returnTo';

export function rememberReturnTo(path: string) {
  sessionStorage.setItem(RETURN_TO_KEY, path);
}

export function takeReturnTo(): string | null {
  const value = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return value;
}

/**
 * The console's single MSAL instance.
 *
 * "Single" is load-bearing: OD-18 rejected two differently-configured MSAL
 * setups inside one app precisely because that is the shape that produced the
 * M0-33 test-harness bug. This app has exactly one, on its own origin, and the
 * customer app has exactly one on its own origin.
 */
export const pca = new PublicClientApplication(msalConfig);

/**
 * Initialises MSAL and processes the redirect response before rendering.
 * Rendering children first would let route guards read a signed-out state
 * during the split second before the redirect is handled, causing a redirect
 * loop.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      await pca.initialize();

      pca.addEventCallback((message) => {
        if (message.eventType === EventType.LOGIN_SUCCESS && message.payload) {
          const { account } = message.payload as AuthenticationResult;
          if (account) {
            pca.setActiveAccount(account);
          }
        }
      });

      const response = await pca.handleRedirectPromise();
      if (response?.account) {
        pca.setActiveAccount(response.account);
      } else if (!pca.getActiveAccount()) {
        const [first] = pca.getAllAccounts();
        if (first) {
          pca.setActiveAccount(first);
        }
      }

      if (!cancelled) {
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return null;
  }

  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}
